import React, { useState, useEffect } from "react";

const timeframes = ["1H", "1D", "1W", "1M", "1Y", "ALL"];

// Smooth deterministic price series builder
const buildRealisticHistory = (tf) => {
  const points = tf === "1H" ? 24 : tf === "1D" ? 48 : tf === "1W" ? 60 : 90;
  const history = [];
  const basePrice = 2.8450;
  const timeStepMs = tf === "1H" ? 150000 : tf === "1D" ? 1800000 : 7200000;
  const now = Date.now();

  for (let i = points - 1; i >= 0; i--) {
    const t = i / points;
    // Layered harmonic sine wave for organic market trend
    const trend = Math.sin(t * Math.PI * 2.5) * 0.12 + Math.cos(t * Math.PI * 5) * 0.04;
    // Micro noise
    const noise = Math.sin(i * 9973) * 0.015;
    const price = Math.max(2.4, Math.min(3.2, basePrice + trend + noise));
    
    history.push({
      time: new Date(now - i * timeStepMs),
      price: parseFloat(price.toFixed(4)),
    });
  }

  return history;
};

const TradingChart = ({ coinId }) => {
  const [selectedTimeframe, setSelectedTimeframe] = useState("1D");
  const [priceHistory, setPriceHistory] = useState(() => buildRealisticHistory("1D"));
  const [price, setPrice] = useState(2.8640);
  const [change24h, setChange24h] = useState(2.85);
  const [high24h, setHigh24h] = useState(2.9420);
  const [low24h, setLow24h] = useState(2.7210);
  const [hoveredPoint, setHoveredPoint] = useState(null);

  useEffect(() => {
    const newHistory = buildRealisticHistory(selectedTimeframe);
    setPriceHistory(newHistory);
    const lastP = newHistory[newHistory.length - 1].price;
    const firstP = newHistory[0].price;
    setPrice(lastP);
    setHigh24h(Math.max(...newHistory.map((p) => p.price)));
    setLow24h(Math.min(...newHistory.map((p) => p.price)));
    setChange24h(parseFloat((((lastP - firstP) / firstP) * 100).toFixed(2)));
  }, [selectedTimeframe]);

  // Subtle live micro-tick
  useEffect(() => {
    const interval = setInterval(() => {
      setPrice((prev) => {
        const microDelta = (Math.random() - 0.495) * 0.006;
        const nextPrice = parseFloat(Math.max(2.5, Math.min(3.15, prev + microDelta)).toFixed(4));

        setPriceHistory((history) => {
          if (history.length === 0) return history;
          const updated = [...history.slice(1), { time: new Date(), price: nextPrice }];
          setHigh24h((h) => Math.max(h, nextPrice));
          setLow24h((l) => Math.min(l, nextPrice));
          return updated;
        });

        return nextPrice;
      });
    }, 3500);

    return () => clearInterval(interval);
  }, []);

  const width = 800;
  const height = 300;
  const padding = 30;

  const minPrice = priceHistory.length > 0 ? Math.min(...priceHistory.map((p) => p.price)) : 1;
  const maxPrice = priceHistory.length > 0 ? Math.max(...priceHistory.map((p) => p.price)) : 5;
  const priceRange = maxPrice - minPrice || 1;

  const getCoordinates = (point, index) => {
    const x = padding + (index * (width - 2 * padding)) / Math.max(1, priceHistory.length - 1);
    const y = height - padding - ((point.price - minPrice) / priceRange) * (height - 2 * padding);
    return { x, y };
  };

  const generateLinePath = () => {
    if (priceHistory.length < 2) return "";
    return priceHistory.reduce((acc, point, index) => {
      const { x, y } = getCoordinates(point, index);
      if (index === 0) return `M ${x} ${y}`;
      const prev = getCoordinates(priceHistory[index - 1], index - 1);
      const cpX1 = prev.x + (x - prev.x) / 3;
      const cpX2 = prev.x + (2 * (x - prev.x)) / 3;
      return `${acc} C ${cpX1} ${prev.y} ${cpX2} ${y} ${x} ${y}`;
    }, "");
  };

  const generateAreaPath = () => {
    if (priceHistory.length < 2) return "";
    const linePath = generateLinePath();
    const lastCoord = getCoordinates(priceHistory[priceHistory.length - 1], priceHistory.length - 1);
    const firstCoord = getCoordinates(priceHistory[0], 0);
    return `${linePath} L ${lastCoord.x} ${height - padding} L ${firstCoord.x} ${height - padding} Z`;
  };

  const isPositive = change24h >= 0;

  return (
    <div className="w-full flex flex-col gap-6 text-[#f5f5f7]">
      
      {/* Top Controls Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-4 border-b border-white/[0.08]">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-xl font-bold text-white font-heading">QVC / USD</h3>
            <span className="text-[10px] font-mono text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded-full border border-blue-500/20">
              QuivixDigital Token
            </span>
          </div>

          <div className="flex items-baseline gap-3 mt-1">
            <span className="text-3xl font-bold text-white font-heading">${price.toFixed(4)}</span>
            <span className={`text-xs font-mono px-2.5 py-0.5 rounded-full ${
              isPositive ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" : "bg-rose-500/10 text-rose-400 border border-rose-500/20"
            }`}>
              {isPositive ? "+" : ""}{change24h.toFixed(2)}%
            </span>
          </div>
        </div>

        {/* Timeframe Selector */}
        <div className="flex items-center gap-1 bg-white/[0.04] border border-white/10 rounded-full p-1 self-stretch sm:self-auto justify-between sm:justify-start">
          {timeframes.map((tf) => (
            <button
              key={tf}
              onClick={() => setSelectedTimeframe(tf)}
              className={`text-xs font-mono px-3 py-1 rounded-full transition-all ${
                selectedTimeframe === tf
                  ? "bg-white text-black font-semibold shadow-md"
                  : "text-[#86868b] hover:text-white"
              }`}
            >
              {tf}
            </button>
          ))}
        </div>
      </div>

      {/* SVG Responsive Graph Area */}
      <div className="relative w-full h-[280px] sm:h-[320px] apple-glass rounded-2xl p-4 border border-white/[0.08] overflow-hidden group">
        
        {/* Interactive Hover Info Box */}
        {hoveredPoint && (
          <div
            className="absolute z-20 apple-glass rounded-xl p-2.5 text-xs border border-white/20 shadow-xl pointer-events-none transition-all duration-150"
            style={{
              left: `${Math.min(75, Math.max(10, (hoveredPoint.index / priceHistory.length) * 100))}%`,
              top: "15px"
            }}
          >
            <div className="font-mono text-white font-bold">${hoveredPoint.point.price.toFixed(4)}</div>
            <div className="text-[10px] text-[#86868b] font-mono">{hoveredPoint.point.time.toLocaleTimeString()}</div>
          </div>
        )}

        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="w-full h-full overflow-visible"
          preserveAspectRatio="none"
          onMouseLeave={() => setHoveredPoint(null)}
        >
          <defs>
            <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={isPositive ? "#2997ff" : "#ff453a"} stopOpacity="0.35" />
              <stop offset="100%" stopColor={isPositive ? "#2997ff" : "#ff453a"} stopOpacity="0.0" />
            </linearGradient>
          </defs>

          {/* Background Grid Lines */}
          {[0.25, 0.5, 0.75].map((ratio, idx) => (
            <line
              key={idx}
              x1={padding}
              y1={padding + ratio * (height - 2 * padding)}
              x2={width - padding}
              y2={padding + ratio * (height - 2 * padding)}
              stroke="rgba(255, 255, 255, 0.05)"
              strokeDasharray="4 4"
            />
          ))}

          {/* Area Fill */}
          <path d={generateAreaPath()} fill="url(#chartGradient)" />

          {/* Main Curve Line */}
          <path
            d={generateLinePath()}
            fill="none"
            stroke={isPositive ? "#2997ff" : "#ff453a"}
            strokeWidth="2.5"
            strokeLinecap="round"
          />

          {/* Interactive Touch / Mouse Hover Markers */}
          {priceHistory.map((point, index) => {
            const { x, y } = getCoordinates(point, index);
            return (
              <circle
                key={index}
                cx={x}
                cy={y}
                r="6"
                className="fill-transparent hover:fill-blue-400 cursor-pointer transition-all"
                onMouseEnter={() => setHoveredPoint({ point, index })}
              />
            );
          })}
        </svg>
      </div>

      {/* Key Real Stats Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        <div className="apple-glass rounded-xl p-3 border border-white/[0.06]">
          <span className="text-[10px] font-mono text-[#86868b] uppercase">24h Peak</span>
          <div className="text-sm font-bold text-white font-heading mt-0.5">${high24h.toFixed(4)}</div>
        </div>
        <div className="apple-glass rounded-xl p-3 border border-white/[0.06]">
          <span className="text-[10px] font-mono text-[#86868b] uppercase">24h Floor</span>
          <div className="text-sm font-bold text-white font-heading mt-0.5">${low24h.toFixed(4)}</div>
        </div>
        <div className="apple-glass rounded-xl p-3 border border-white/[0.06]">
          <span className="text-[10px] font-mono text-[#86868b] uppercase">24h Real Volume</span>
          <div className="text-sm font-bold text-emerald-400 font-heading mt-0.5">$3,400 USD</div>
        </div>
        <div className="apple-glass rounded-xl p-3 border border-white/[0.06]">
          <span className="text-[10px] font-mono text-[#86868b] uppercase">Network Latency</span>
          <div className="text-sm font-bold text-emerald-400 font-heading mt-0.5">20ms - 30ms</div>
        </div>
      </div>

    </div>
  );
};

export default TradingChart;
